import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { FormGroup, Select, Button } from '../../components/ui'
import { translateApiError } from '../../utils/errorMessages'
import './Admin.css'

function AdminDeletedRecords() {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [entityType, setEntityType] = useState('')

  useEffect(() => {
    fetchRecords()
  }, [entityType])

  const fetchRecords = async () => {
    try {
      setLoading(true)
      const params = entityType ? { entityType, limit: 100 } : { limit: 100 }
      const response = await api.get('/admin/deleted-records', { params })
      setRecords(response.data.records || [])
    } catch (error) {
      console.error('Error fetching deleted records:', error)
      alert('Ошибка загрузки удаленных записей: ' + translateApiError(error.response?.data?.error || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleRestore = async (entityType, recordId) => {
    if (!confirm('Восстановить эту запись?')) return
    
    try {
      const encType = encodeURIComponent(entityType)
      const encId = encodeURIComponent(recordId)
      await api.post(`/admin/deleted-records/${encType}/${encId}/restore`)
      alert('Запись восстановлена')
      fetchRecords()
    } catch (error) {
      const msg = error.response?.data?.error || error.message
      const status = error.response?.status
      alert(`Ошибка восстановления записи${status ? ` (${status})` : ''}: ${translateApiError(msg)}`)
    }
  }

  const handleAccept = async (entityType, recordId) => {
    if (!confirm('Окончательно удалить эту запись? Это действие нельзя отменить.')) return
    
    try {
      const encType = encodeURIComponent(entityType)
      const encId = encodeURIComponent(recordId)
      await api.post(`/admin/deleted-records/${encType}/${encId}/accept`)
      alert('Запись удалена окончательно')
      fetchRecords()
    } catch (error) {
      const msg = error.response?.data?.error || error.message
      const status = error.response?.status
      alert(`Ошибка удаления записи${status ? ` (${status})` : ''}: ${translateApiError(msg)}`)
    }
  }

  const getEntityTypeName = (type) => {
    const names = {
      clients: 'Клиенты',
      contacts: 'Контакты',
      objects: 'Объекты',
      contracts: 'Договоры',
      stages: 'Стадии',
      acts: 'Акты',
      employees: 'Сотрудники',
      income_distributions: 'Распределение доходов'
    }
    return names[type] || type
  }

  /** Человекочитаемое название/описание записи по типу сущности (без сырых объектов). */
  const getDisplayName = (record) => {
    if (!record || typeof record !== 'object') return '—'
    if (record.display_name != null && String(record.display_name).trim()) return String(record.display_name).trim()
    const t = record.entity_type
    const str = (v) => (v != null && String(v).trim() ? String(v).trim() : '')
    switch (t) {
      case 'clients': return str(record.name) || '—'
      case 'contacts': return str(record.name) || str(record.email) || '—'
      case 'objects': return str(record.name) || str(record.title) || '—'
      case 'contracts': return str(record.number) || '—'
      case 'stages': return [str(record.number), str(record.type)].filter(Boolean).join(' · ') || '—'
      case 'acts': return str(record.number) || str(record.title) || '—'
      case 'employees': return str(record.full_name) || '—'
      case 'income_distributions': return str(record.id) || '—'
      default: return str(record.name) || str(record.full_name) || str(record.number) || str(record.title) || '—'
    }
  }

  if (loading) return <div>Загрузка...</div>

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-filter-row">
          <FormGroup label="Тип сущности">
            <Select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              aria-label="Тип сущности"
              style={{ minWidth: '220px' }}
            >
              <option value="">Все типы</option>
              <option value="clients">Клиенты</option>
              <option value="contacts">Контакты</option>
              <option value="objects">Объекты</option>
              <option value="contracts">Договоры</option>
              <option value="stages">Стадии</option>
              <option value="acts">Акты</option>
              <option value="employees">Сотрудники</option>
              <option value="income_distributions">Распределение доходов</option>
            </Select>
          </FormGroup>
          <Button type="button" variant="secondary" onClick={fetchRecords}>
            Обновить
          </Button>
        </div>
      </div>

      <div className="admin-table-frame">
        <div className="table-responsive">
        <table className="ui-table data-table">
          <thead>
            <tr>
              <th>Тип</th>
              <th>ID</th>
              <th>Название/Описание</th>
              <th>Дата удаления</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                  Удаленных записей не найдено
                </td>
              </tr>
            ) : (
              records.map((record, index) => (
                <tr key={`${record.entity_type}-${record.id}-${index}`}>
                  <td>{getEntityTypeName(record.entity_type)}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {record.id.substring(0, 8)}...
                  </td>
                  <td>
                    {getDisplayName(record)}
                  </td>
                  <td>
                    {record.deleted_at 
                      ? new Date(record.deleted_at).toLocaleString('ru-RU')
                      : '—'}
                  </td>
                  <td>
                    <button
                      onClick={() => handleRestore(record.entity_type, record.id)}
                      className="btn-secondary"
                      style={{ marginRight: '8px' }}
                    >
                      Восстановить
                    </button>
                    <button
                      onClick={() => handleAccept(record.entity_type, record.id)}
                      className="btn-danger"
                    >
                      Удалить окончательно
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  )
}

export default AdminDeletedRecords

